import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { General } from 'icons/untitledui';
import * as Buttons from 'theme/components/Buttons.style';
import * as Styled from 'form/styles/FormEditView.style';

import InlineDateField from './InlineDateField';
import { MULTIDATE_SEPARATOR } from './constants';

interface MultidateFieldProps {
  slug: string;
}

export default function MultidateField({
  slug,
}: MultidateFieldProps): JSX.Element {
  const { values, setFieldValue } = useFormikContext<Record<string, any>>();
  const { t } = useTranslation('pageForm');
  const [inputCount, setInputCount] = useState(1);

  const stored = String(values[slug] || '')
    ? String(values[slug]).split(MULTIDATE_SEPARATOR)
    : [];
  const count = Math.max(inputCount, stored.length, 1);
  const display: string[] = [];
  for (let i = 0; i < count; i += 1) display.push(stored[i] || '');

  const save = (next: string[]) =>
    setFieldValue(slug, next.join(MULTIDATE_SEPARATOR));

  const handleChange = (index: number, value: string) => {
    if (value.includes('|')) return;
    const next = [...display];
    next[index] = value;
    save(next);
  };

  const handleRemove = (index: number) => {
    const next = display.filter((_, i) => i !== index);
    save(next);
    setInputCount(Math.max(count - 1, 1));
  };

  return (
    <Styled.ListFieldWrapper>
      {display.map((value, index) => (
        <Styled.ListRow key={index}>
          <Styled.ListRowItem>
            <InlineDateField
              value={value}
              onChange={(next) => handleChange(index, next)}
              placeholderText={t('edit.datePlaceholder')}
            />
          </Styled.ListRowItem>
          {count > 1 && (
            <Buttons.GhostIcon
              $size="tiny"
              type="button"
              aria-label={t('edit.removeDate')}
              onClick={() => handleRemove(index)}
            >
              <General.XClose />
            </Buttons.GhostIcon>
          )}
        </Styled.ListRow>
      ))}
      <Buttons.Ghost
        $size="small"
        type="button"
        onClick={() => setInputCount(count + 1)}
      >
        <General.Plus />
        {t('edit.addDate')}
      </Buttons.Ghost>
    </Styled.ListFieldWrapper>
  );
}
